import React, { useEffect, useState } from 'react'
import SideBar from '../Components/SideBar';
import Header from '../Components/Header';
import { Button, FormControl, InputLabel, MenuItem, Paper, Select, TextField, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TablePagination, IconButton, InputAdornment, Tooltip, Autocomplete } from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from 'axios';
import BASEURL from '../Connection/BASEURL';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Payments = () => {
    const [loading, setloading] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [payments, setPayments] = useState([]);
    const [clients, setClients] = useState([]);
    const [deceased, setDeceased] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchvalue, setsearchvalue] = useState("");

    const paymentMethods = [
        'Cash',
        'Mobile Money',
        'Bank Transfer',
        'Cheque',
    ];

    const [form, setForm] = useState({
        name_of_client: '',
        amount: '',
        balance_to_payment: '',
        name_of_deceased: '',
        payment_method: '',
        discount_percentage: '',
    });
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedDeceased, setSelectedDeceased] = useState(null);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [editForm, setEditForm] = useState({
        name_of_client: '',
        amount: '',
        balance_to_payment: '',
        name_of_deceased: '',
        payment_method: '',
        discount_percentage: '',
    });
    const [editSelectedClient, setEditSelectedClient] = useState(null);
    const [editSelectedDeceased, setEditSelectedDeceased] = useState(null);

    const handleChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleEditChange = (key, value) => {
        setEditForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleEditPayment = (payment) => {
        setSelectedPayment(payment);
        setEditForm({
            name_of_client: payment.name_of_client || '',
            amount: payment.amount || '',
            balance_to_payment: payment.balance_to_payment || '',
            name_of_deceased: payment.name_of_deceased || '',
            payment_method: payment.payment_method || '',
            discount_percentage: payment.discount_percentage || '',
        });
        
        // Find and set the selected client and deceased
        const client = clients.find(c => {
            const clientName = c.fname && c.lname ? `${c.fname} ${c.lname}`.trim() : c.username || c.name;
            return clientName === payment.name_of_client;
        });
        setEditSelectedClient(client || null);
        
        const deceasedPerson = deceased.find(d => {
            const deceasedName = d.fname && d.lname ? `${d.fname} ${d.lname}`.trim() : d.name;
            return deceasedName === payment.name_of_deceased;
        });
        setEditSelectedDeceased(deceasedPerson || null);
        
        setOpenEditDialog(true);
    };

    const handleUpdatePayment = async () => {
        if (!editSelectedClient || !editForm.amount || !editForm.balance_to_payment || !editSelectedDeceased || !editForm.payment_method || !editForm.discount_percentage) {
            toast.error('Please fill all required fields');
            return;
        }
        try {
            setloading(true);
            const clientName = editSelectedClient.fname && editSelectedClient.lname 
                ? `${editSelectedClient.fname} ${editSelectedClient.lname}`.trim()
                : editSelectedClient.username || editSelectedClient.name || 'Unknown Client';
            
            const deceasedName = editSelectedDeceased.fname && editSelectedDeceased.lname 
                ? `${editSelectedDeceased.fname} ${editSelectedDeceased.lname}`.trim()
                : editSelectedDeceased.name || 'Unknown Deceased';
            
            const payload = {
                name_of_client: clientName,
                amount: Number(editForm.amount),
                balance_to_payment: Number(editForm.balance_to_payment),
                name_of_deceased: deceasedName,
                payment_method: editForm.payment_method,
                discount_percentage: Number(editForm.discount_percentage),
            };
            await axios.put(`${BASEURL}/payments/${selectedPayment.tblid}`, payload);
            toast.success('Payment updated successfully');
            setOpenEditDialog(false);
            setSelectedPayment(null);
            await fetchPayments();
        } catch (error) {
            console.log(error);
            toast.error('Failed to update payment');
        } finally {
            setloading(false);
        }
    };

    const handleDeletePayment = (payment) => {
        setSelectedPayment(payment);
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            setloading(true);
            await axios.delete(`${BASEURL}/payments/${selectedPayment.tblid}`);
            toast.success('Payment deleted successfully');
            setOpenDeleteDialog(false);
            setSelectedPayment(null);
            await fetchPayments();
        } catch (error) {
            console.log(error);
            toast.error('Failed to delete payment');
        } finally {
            setloading(false);
        }
    };

    const handleExportToExcel = () => {
        // Create CSV content
        const headers = ['ID', 'Client Name', 'Amount', 'Balance to Payment', 'Deceased Name', 'Payment Method', 'Discount %', 'Created Date'];
        const csvContent = [
            headers.join(','),
            ...filteredPayments.map(payment => [
                payment.tblid,
                `"${payment.name_of_client}"`,
                payment.amount,
                payment.balance_to_payment,
                `"${payment.name_of_deceased}"`,
                `"${payment.payment_method}"`,
                payment.discount_percentage,
                new Date(payment.createdAt).toLocaleDateString()
            ].join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `payments_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Payments exported successfully');
    };

    const handleSubmit = async () => {
        if (!selectedClient || !form.amount || !form.balance_to_payment || !selectedDeceased || !form.payment_method || !form.discount_percentage) {
            toast.error('Please fill all required fields');
            return;
        }
        try {
            setloading(true);
            const clientName = selectedClient.fname && selectedClient.lname 
                ? `${selectedClient.fname} ${selectedClient.lname}`.trim()
                : selectedClient.username || selectedClient.name || 'Unknown Client';
            
            const deceasedName = selectedDeceased.fname && selectedDeceased.lname 
                ? `${selectedDeceased.fname} ${selectedDeceased.lname}`.trim()
                : selectedDeceased.name || 'Unknown Deceased';
            
            const payload = {
                name_of_client: clientName,
                amount: Number(form.amount),
                balance_to_payment: Number(form.balance_to_payment),
                name_of_deceased: deceasedName,
                payment_method: form.payment_method,
                discount_percentage: Number(form.discount_percentage),
            };
            await axios.post(`${BASEURL}/payments/`, payload);
            toast.success('Payment recorded successfully');
            setForm({
                name_of_client: '',
                amount: '',
                balance_to_payment: '',
                name_of_deceased: '',
                payment_method: '',
                discount_percentage: '',
            });
            setSelectedClient(null);
            setSelectedDeceased(null);
            setOpenDialog(false);
            await fetchPayments();
        } catch (error) {
            console.log(error);
            toast.error('Failed to record payment');
        } finally {
            setloading(false);
        }
    };

    const fetchPayments = async () => {
        try {
            setListLoading(true);
            const response = await axios.get(`${BASEURL}/payments`);
            console.log('Real Payments data:', response.data); // Debug log
            setPayments(response.data || []);
        } catch (error) {
            console.log('Error fetching payments:', error);
            toast.error('Failed to load payments');
        } finally {
            setListLoading(false);
        }
    };

    // Comment out the old payment fetching function
    // const fetchPayments = async () => {
    //     try {
    //         setListLoading(true);
    //         const response = await axios.get(`${BASEURL}/deceased2/deceased-payment-fields`);
    //         console.log('Payments data:', response.data); // Debug log
    //         setPayments(response.data || []);
    //     } catch (error) {
    //         console.log('Error fetching payments:', error);
    //         toast.error('Failed to load payments');
    //     } finally {
    //         setListLoading(false);
    //     }
    // };

    useEffect(() => {
        fetchPayments();
    }, []);

    const fetchClients = async () => {
        try {
            const response = await axios.get(`${BASEURL}/clients/all_clients`);
            setClients(response.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchDeceased = async () => {
        try {
            const response = await axios.get(`${BASEURL}/deceased`);
            setDeceased(response.data || []);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        fetchDeceased();
    }, []);

    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    const filteredPayments = payments.filter((payment) => {
        const clientName = payment.name_of_client?.toLowerCase() || '';
        const deceasedName = payment.name_of_deceased?.toLowerCase() || '';
        const paymentMethod = payment.payment_method?.toLowerCase() || '';
        return clientName.includes(searchvalue.toLowerCase()) || 
               deceasedName.includes(searchvalue.toLowerCase()) || 
               paymentMethod.includes(searchvalue.toLowerCase());
    });

    // Debug log to see what data we have
    console.log('Payments state:', payments);
    console.log('Filtered payments:', filteredPayments);

    return (
        <>
            <ToastContainer />
            <div className=' grid grid-cols-12'>
                <div className=' col-span-2'>
                    <SideBar />
                </div>
                <div className=' col-span-12 lg:col-span-10 bg-[#D2D2CF]'>
                    <Header />
                    <div className=' mx-8'>
                        <div className=" flex items-center justify-between py-6">
                            <h1 className=" text-4xl font-bold  text-[#111927] py-2">Payments</h1>
                            <div className="flex gap-3">
                                <Button 
                                    onClick={handleExportToExcel} 
                                    variant="outlined" 
                                    startIcon={<FileDownload />}
                                    style={{ borderColor: '#0F1D48', color: '#0F1D48' }}
                                >
                                    Export to Excel
                                </Button>
                            <Button onClick={() => setOpenDialog(true)} variant="contained" style={{ backgroundColor: '#0F1D48', color: 'white' }}>Add Payment</Button>
                            </div>
                        </div>

                        {listLoading ? (
                            <div className=" grid place-items-center mt-36">
                                <CircularProgress size={50} style={{ color: "#122E76" }} />
                                <p className="mt-4 text-gray-600">Loading payments...</p>
                            </div>
                        ) : (
                            <TableContainer
                                elevation={3}
                                component={Paper}
                                style={{ borderRadius: 10,marginTop: 20,marginBottom: 20 }}
                            >
                                <div className=" flex items-center  space-x-7 px-6 p-9 pb-7">
                                    <TextField
                                        onChange={(e) => {
                                            setsearchvalue(e.target.value)
                                        }}
                                        value={searchvalue}
                                        style={{ width: "85%" }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <IconButton edge="start" size="small">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke-width="1.5"
                                                            stroke="currentColor"
                                                            className="w-6 h-6"
                                                        >
                                                            <path
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                                                            />
                                                        </svg>
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        size="medium"
                                        fullWidth
                                        placeholder="Search for Payments"
                                        label="Search for Payments"
                                    />
                                    {/* <Button disabled={!searchvalue} variant="contained" style={{
                                        backgroundColor: "#083050",
                                        color: "white",
                                        width: "15%",
                                        padding: 14
                                    }}>Search</Button> */}
                        </div>

                                <Table>
                                    <TableHead style={{ backgroundColor: "#F8F9FA" }}>
                                        <TableRow>
                                            <TableCell
                                                style={{
                                                    fontWeight: "bold",
                                                    fontSize: "16px",
                                                    fontFamily: "'Poppins', sans-serif",
                                                    paddingLeft: 21,
                                                }}
                                            >
                                                Client Name
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    fontWeight: "bold",
                                                    fontSize: "16px",
                                                    fontFamily: "'Poppins', sans-serif",
                                                }}
                                            >
                                                Amount
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    fontWeight: "bold",
                                                    fontSize: "16px",
                                                    fontFamily: "'Poppins', sans-serif",
                                                    paddingLeft: 21,
                                                }}
                                            >
                                                Balance to Payment
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    fontWeight: "bold",
                                                    fontSize: "16px",
                                                    fontFamily: "'Poppins', sans-serif",
                                                    paddingLeft: 21,
                                                }}
                                            >
                                                Deceased Name
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    fontWeight: "bold",
                                                    fontSize: "16px",
                                                    fontFamily: "'Poppins', sans-serif",
                                                    paddingLeft: 21,
                                                }}
                                            >
                                                Payment Method
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    fontWeight: "bold",
                                                    fontSize: "16px",
                                                    fontFamily: "'Poppins', sans-serif",
                                                    paddingLeft: 21,
                                                }}
                                            >
                                                Discount %
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    fontWeight: "bold",
                                                    fontSize: "16px",
                                                    fontFamily: "'Poppins', sans-serif",
                                                    paddingLeft: 21,
                                                }}
                                            >
                                                Created Date
                                            </TableCell>
                                            <TableCell
                                                style={{
                                                    fontWeight: "bold",
                                                    fontSize: "16px",
                                                    fontFamily: "'Poppins', sans-serif",
                                                    paddingLeft: 21,
                                                }}
                                            >
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredPayments.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={8} style={{ textAlign: 'center', padding: '20px' }}>
                                                    {payments.length === 0 ? 'No payments data available.' : 'No payments found matching your search.'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredPayments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((p, index) => {
                                                    return (
                                                        <TableRow key={p.tblid || p.id || index} hover>
                                                            <TableCell
                                                                style={{
                                                                    fontFamily: "'Poppins', sans-serif",
                                                                    paddingLeft: 21,
                                                                    fontWeight: "bold"
                                                                }}
                                                            >
                                                                {p.name_of_client || 'N/A'}
                                                            </TableCell>
                                                            <TableCell
                                                                style={{ fontFamily: "'Poppins', sans-serif" }}
                                                            >
                                                                {p.amount > 0 ? `GH₵ ${Number(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                                                            </TableCell>
                                                            <TableCell
                                                                style={{ fontFamily: "'Poppins', sans-serif" }}
                                                            >
                                                                {p.balance_to_payment > 0 ? `GH₵ ${Number(p.balance_to_payment).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                                                            </TableCell>
                                                            <TableCell
                                                                style={{ fontFamily: "'Poppins', sans-serif" }}
                                                            >
                                                                {p.name_of_deceased || 'N/A'}
                                                            </TableCell>
                                                            <TableCell
                                                                style={{ fontFamily: "'Poppins', sans-serif" }}
                                                            >
                                                                {p.payment_method || 'N/A'}
                                                            </TableCell>
                                                            <TableCell
                                                                style={{ fontFamily: "'Poppins', sans-serif" }}
                                                            >
                                                                {p.discount_percentage > 0 ? `${Number(p.discount_percentage).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %` : 'N/A'}
                                                            </TableCell>
                                                            <TableCell
                                                                style={{ fontFamily: "'Poppins', sans-serif" }}
                                                            >
                                                                {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : 'N/A'}
                                                            </TableCell>
                                                            <TableCell
                                                                style={{ fontFamily: "'Poppins', sans-serif" }}
                                                            >
                                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                                    <Button
                                                                        onClick={() => handleEditPayment(p)}
                                                                        variant="contained"
                                                                        size="small"
                                                                        style={{
                                                                            backgroundColor: "#0F1D48",
                                                                            color: "white",
                                                                            fontSize: "12px",
                                                                            padding: "4px 8px",
                                                                            minWidth: "60px"
                                                                        }}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                    <Button
                                                                        onClick={() => handleDeletePayment(p)}
                                                                        variant="contained"
                                                                        size="small"
                                                                        style={{
                                                                            backgroundColor: "#DC2626",
                                                                            color: "white",
                                                                            fontSize: "12px",
                                                                            padding: "4px 8px",
                                                                            minWidth: "60px"
                                                                        }}
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                </div>
                                                            </TableCell>
                                                </TableRow>
                                                    );
                                                })
                                        )}
                                    </TableBody>
                                </Table>
                                <TablePagination
                                    style={{ fontFamily: "'Poppins', sans-serif" }}
                                    rowsPerPageOptions={[5, 10, 25]}
                                    component="div"
                                    count={filteredPayments.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </TableContainer>
                        )}

                        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth='md'>
                            <DialogTitle>Add Payment</DialogTitle>
                            <DialogContent>
                                <div className=' grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2'>
                                    <Autocomplete
                                        options={clients}
                                        getOptionLabel={(option) => {
                                            if (option.fname && option.lname) {
                                                return `${option.fname} ${option.lname}`.trim();
                                            }
                                            return option.username || option.name || `Client ${option.tblid || option.id}`;
                                        }}
                                        value={selectedClient}
                                        onChange={(event, newValue) => {
                                            setSelectedClient(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Client"
                                                placeholder="Search for client..."
                                                required
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            const displayName = option.fname && option.lname 
                                                ? `${option.fname} ${option.lname}`.trim()
                                                : option.username || option.name || `Client ${option.tblid || option.id}`;
                                            return (
                                                <li {...props} key={option.tblid || option.id}>
                                                    {displayName}
                                                </li>
                                            );
                                        }}
                                        isOptionEqualToValue={(option, value) => 
                                            (option.tblid || option.id) === (value?.tblid || value?.id)
                                        }
                                    />
                                    <TextField
                                        label='Amount'
                                        type='number'
                                        value={form.amount}
                                        onChange={(e) => handleChange('amount', e.target.value)}
                                        placeholder='Enter amount'
                                        fullWidth
                                        required
                                    />
                                    <TextField
                                        label='Balance To Payment'
                                        type='number'
                                        value={form.balance_to_payment}
                                        onChange={(e) => handleChange('balance_to_payment', e.target.value)}
                                        placeholder='Enter balance to payment'
                                        fullWidth
                                        required
                                    />
                                    <Autocomplete
                                        options={deceased}
                                        getOptionLabel={(option) => {
                                            if (option.fname && option.lname) {
                                                return `${option.fname} ${option.lname}`.trim();
                                            }
                                            return option.name || `Deceased ${option.tblid || option.id}`;
                                        }}
                                        value={selectedDeceased}
                                        onChange={(event, newValue) => {
                                            setSelectedDeceased(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Deceased"
                                                placeholder="Search for deceased..."
                                                required
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            const displayName = option.fname && option.lname 
                                                ? `${option.fname} ${option.lname}`.trim()
                                                : option.name || `Deceased ${option.tblid || option.id}`;
                                                return (
                                                <li {...props} key={option.tblid || option.id}>
                                                    {displayName}
                                                </li>
                                                );
                                        }}
                                        isOptionEqualToValue={(option, value) => 
                                            (option.tblid || option.id) === (value?.tblid || value?.id)
                                        }
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel id='pmethod'>Payment Method</InputLabel>
                                        <Select
                                            labelId='pmethod'
                                            label='Payment Method'
                                            value={form.payment_method}
                                            onChange={(e) => handleChange('payment_method', e.target.value)}
                                        >
                                            {paymentMethods.map((m) => (
                                                <MenuItem key={m} value={m}>{m}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label='Discount Percentage'
                                        type='number'
                                        value={form.discount_percentage}
                                        onChange={(e) => handleChange('discount_percentage', e.target.value)}
                                        placeholder='Enter discount percentage'
                                        fullWidth
                                        required
                                    />
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                                {loading ? (
                                    <Button variant='contained' style={{ backgroundColor: '#0F1D48', color: 'white' }}>
                                        <CircularProgress size={17} style={{ color: 'white' }} />
                                    </Button>
                                ) : (
                                    <Button onClick={handleSubmit} variant='contained' style={{ backgroundColor: '#0F1D48', color: 'white' }}>
                                        Save
                                    </Button>
                                )}
                            </DialogActions>
                        </Dialog>

                        {/* Edit Payment Modal */}
                        <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth='md'>
                            <DialogTitle>Edit Payment</DialogTitle>
                            <DialogContent>
                                <div className=' grid grid-cols-1 lg:grid-cols-2 gap-6 pt-2'>
                                    <Autocomplete
                                        options={clients}
                                        getOptionLabel={(option) => {
                                            if (option.fname && option.lname) {
                                                return `${option.fname} ${option.lname}`.trim();
                                            }
                                            return option.username || option.name || `Client ${option.tblid || option.id}`;
                                        }}
                                        value={editSelectedClient}
                                        onChange={(event, newValue) => {
                                            setEditSelectedClient(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Client"
                                                placeholder="Search for client..."
                                                required
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            const displayName = option.fname && option.lname 
                                                ? `${option.fname} ${option.lname}`.trim()
                                                : option.username || option.name || `Client ${option.tblid || option.id}`;
                                            return (
                                                <li {...props} key={option.tblid || option.id}>
                                                    {displayName}
                                                </li>
                                            );
                                        }}
                                        isOptionEqualToValue={(option, value) => 
                                            (option.tblid || option.id) === (value?.tblid || value?.id)
                                        }
                                    />
                                    <TextField
                                        label='Amount'
                                        type='number'
                                        value={editForm.amount}
                                        onChange={(e) => handleEditChange('amount', e.target.value)}
                                        placeholder='Enter amount'
                                        fullWidth
                                        required
                                    />
                                    <TextField
                                        label='Balance To Payment'
                                        type='number'
                                        value={editForm.balance_to_payment}
                                        onChange={(e) => handleEditChange('balance_to_payment', e.target.value)}
                                        placeholder='Enter balance to payment'
                                        fullWidth
                                        required
                                    />
                                    <Autocomplete
                                        options={deceased}
                                        getOptionLabel={(option) => {
                                            if (option.fname && option.lname) {
                                                return `${option.fname} ${option.lname}`.trim();
                                            }
                                            return option.name || `Deceased ${option.tblid || option.id}`;
                                        }}
                                        value={editSelectedDeceased}
                                        onChange={(event, newValue) => {
                                            setEditSelectedDeceased(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Deceased"
                                                placeholder="Search for deceased..."
                                                required
                                            />
                                        )}
                                        renderOption={(props, option) => {
                                            const displayName = option.fname && option.lname 
                                                ? `${option.fname} ${option.lname}`.trim()
                                                : option.name || `Deceased ${option.tblid || option.id}`;
                                            return (
                                                <li {...props} key={option.tblid || option.id}>
                                                    {displayName}
                                                </li>
                                            );
                                        }}
                                        isOptionEqualToValue={(option, value) => 
                                            (option.tblid || option.id) === (value?.tblid || value?.id)
                                        }
                                    />
                                    <FormControl fullWidth>
                                        <InputLabel id='edit-pmethod'>Payment Method</InputLabel>
                                        <Select
                                            labelId='edit-pmethod'
                                            label='Payment Method'
                                            value={editForm.payment_method}
                                            onChange={(e) => handleEditChange('payment_method', e.target.value)}
                                        >
                                            {paymentMethods.map((m) => (
                                                <MenuItem key={m} value={m}>{m}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        label='Discount Percentage'
                                        type='number'
                                        value={editForm.discount_percentage}
                                        onChange={(e) => handleEditChange('discount_percentage', e.target.value)}
                                        placeholder='Enter discount percentage'
                                        fullWidth
                                        required
                                    />
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                                {loading ? (
                                    <Button variant='contained' style={{ backgroundColor: '#0F1D48', color: 'white' }}>
                                        <CircularProgress size={17} style={{ color: 'white' }} />
                                    </Button>
                                ) : (
                                    <Button onClick={handleUpdatePayment} variant='contained' style={{ backgroundColor: '#0F1D48', color: 'white' }}>
                                        Update
                                    </Button>
                                )}
                            </DialogActions>
                        </Dialog>

                        {/* Delete Confirmation Modal */}
                        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth='sm' fullWidth>
                            <DialogTitle>Delete Payment</DialogTitle>
                            <DialogContent>
                                <div style={{ padding: '20px 0' }}>
                                    <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '16px', marginBottom: '10px' }}>
                                        Are you sure you want to delete this payment?
                                    </p>
                                    {selectedPayment && (
                                        <div style={{ backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '8px' }}>
                                            <p style={{ fontFamily: "'Poppins', sans-serif", margin: '5px 0' }}>
                                                <strong>Client:</strong> {selectedPayment.name_of_client}
                                            </p>
                                            <p style={{ fontFamily: "'Poppins', sans-serif", margin: '5px 0' }}>
                                                <strong>Amount:</strong> GH₵ {Number(selectedPayment.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </p>
                                            <p style={{ fontFamily: "'Poppins', sans-serif", margin: '5px 0' }}>
                                                <strong>Deceased:</strong> {selectedPayment.name_of_deceased}
                                            </p>
                                            <p style={{ fontFamily: "'Poppins', sans-serif", margin: '5px 0' }}>
                                                <strong>Payment Method:</strong> {selectedPayment.payment_method}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                                {loading ? (
                                    <Button variant='contained' style={{ backgroundColor: '#DC2626', color: 'white' }}>
                                        <CircularProgress size={17} style={{ color: 'white' }} />
                                    </Button>
                                ) : (
                                    <Button onClick={handleConfirmDelete} variant='contained' style={{ backgroundColor: '#DC2626', color: 'white' }}>
                                        Delete
                                    </Button>
                                )}
                            </DialogActions>
                        </Dialog>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Payments


